<template>
  <div class="custom-select" :class="{ 'is-open': isOpen, 'dark-mode': darkMode }">
    <button class="select-trigger" @click="toggleOpen" type="button" :aria-expanded="isOpen">
      <span class="select-value">{{ selectedLabel }}</span>
      <svg class="select-arrow" viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
        <path d="M0 0l5 6 5-6z" fill="currentColor"/>
      </svg>
    </button>
    <transition name="dropdown">
      <ul v-if="isOpen" class="select-dropdown" role="listbox">
        <li v-for="option in options" :key="option.value" role="option" :aria-selected="option.value == value">
          <button class="select-option"
                  :class="{ 'is-selected': option.value == value }"
                  @click="selectOption(option)"
                  type="button">
            {{ option.label }}
          </button>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'CustomSelect',
  props: {
    value: [String, Number],
    options: {
      type: Array,
      required: true
    },
    darkMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false
    }
  },
  computed: {
    selectedLabel() {
      const selected = this.options.find(o => o.value == this.value);
      return selected ? selected.label : '';
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    selectOption(option) {
      this.$emit('input', option.value);
      this.isOpen = false;
    },
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    }
  }
}
</script>

<style scoped>
.custom-select {
  position: relative;
  display: inline-block;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #c0c8d4;
  border-radius: 8px;
  background: #fff;
  color: #00073F;
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.select-trigger:hover {
  border-color: #3498db;
}

.is-open .select-trigger {
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.15);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.select-arrow {
  transition: transform 0.2s ease;
  opacity: 0.5;
  flex-shrink: 0;
}

.is-open .select-arrow {
  transform: rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 2px solid #3498db;
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  max-height: 240px;
  overflow-y: auto;
}

.select-option {
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  color: #00073F;
  font-size: 14px;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.select-option:hover {
  background-color: #f0f4f8;
}

.select-option.is-selected {
  background-color: #3498db;
  color: #fff;
  font-weight: 600;
}

/* Dark mode */
.dark-mode .select-trigger {
  background: #252540;
  color: #e8e8e8;
  border-color: #3a3a55;
}

.dark-mode .select-trigger:hover {
  border-color: #5dade2;
}

.dark-mode.is-open .select-trigger {
  border-color: #5dade2;
  box-shadow: 0 0 0 3px rgba(93, 173, 226, 0.2);
}

.dark-mode .select-dropdown {
  background: #252540;
  border-color: #5dade2;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.dark-mode .select-option {
  color: #e8e8e8;
}

.dark-mode .select-option:hover {
  background-color: #3a3a55;
}

.dark-mode .select-option.is-selected {
  background-color: #3498db;
  color: #fff;
}

/* Animation */
.dropdown-enter-active {
  animation: dropdown-in 0.15s ease;
}

.dropdown-leave-active {
  animation: dropdown-in 0.1s ease reverse;
}

@keyframes dropdown-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .select-trigger {
    padding: 7px 10px;
    font-size: 13px;
  }

  .select-option {
    padding: 10px 12px;
    font-size: 14px;
  }
}
</style>
